import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Wallet,
  ArrowRight,
  Send,
  CheckCircle2,
  AlertTriangle,
  Info,
  ChevronDown,
  ChevronUp,
  Phone,
  ShieldCheck,
} from "lucide-react";

import { useAuth } from "@/contexts/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Modal } from "@/components/Modal";
import {
  getMerchantBalance,
  getPhoneConfig,
  previewWithdrawalFee,
  initiateWithdrawal,
  resendWithdrawalOtp,
  ApiError,
  type MerchantBalanceResponse,
  type MerchantPhoneConfig,
  type WithdrawalFeePreview,
  type WithdrawalPayoutResponse,
  type WithdrawalOtpRequired,
} from "@/lib/api";
import { formatMajor } from "@/lib/format";

type Method = "mpesa" | "orange" | "airtel";
type Currency = "CDF" | "USD";

const METHOD_LABEL: Record<Method, string> = {
  mpesa: "M-Pesa",
  orange: "Orange Money",
  airtel: "Airtel Money",
};

const METHOD_COLOR: Record<Method, string> = {
  mpesa: "#00A551",
  orange: "#FF7A00",
  airtel: "#E4002B",
};

// ---------------------------------------------------------------------------

export function RetraitPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const merchantId = auth.status === "authenticated" ? auth.user.merchant_id : null;

  const balanceQuery = useQuery<MerchantBalanceResponse>({
    queryKey: ["merchant-balance", merchantId],
    queryFn: () => getMerchantBalance(merchantId as number),
    enabled: !!merchantId,
  });

  const phoneQuery = useQuery<MerchantPhoneConfig>({
    queryKey: ["phone-config", merchantId],
    queryFn: getPhoneConfig,
    enabled: !!merchantId,
    retry: false,
  });

  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState<Currency>("CDF");
  const [method, setMethod] = useState<Method>("mpesa");
  const [phoneChoice, setPhoneChoice] = useState<1 | 2>(1);
  const [feePreview, setFeePreview] = useState<WithdrawalFeePreview | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<WithdrawalPayoutResponse | null>(null);
  const [otpNeeded, setOtpNeeded] = useState<WithdrawalOtpRequired | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [otpAttempts, setOtpAttempts] = useState(0);
  const [otpSubmitting, setOtpSubmitting] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Fee preview live sur amount/currency/method
  useEffect(() => {
    const parsed = parseFloat(amount);
    if (!parsed || parsed <= 0) {
      setFeePreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await previewWithdrawalFee({ amount: parsed, currency, method });
        if (!cancelled) setFeePreview(r);
      } catch {
        if (!cancelled) setFeePreview(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [amount, currency, method]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setInterval(() => setResendCooldown((v) => Math.max(0, v - 1)), 1000);
    return () => clearInterval(id);
  }, [resendCooldown]);

  // Si l'utilisateur change d'opérateur et que le n° secondaire n'existe pas
  useEffect(() => {
    if (phoneChoice === 2 && !getSecondaryPhone(phoneQuery.data, method)) {
      setPhoneChoice(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [method, phoneQuery.data]);

  const wallets = balanceQuery.data?.wallets ?? [];
  const balancesByCurrency: Record<string, number> =
    balanceQuery.data?.summary?.total_by_currency ?? {};
  const available = Number(balancesByCurrency[currency] ?? 0);
  const parsedAmount = parseFloat(amount) || 0;
  const feeAmount = feePreview?.method === method && feePreview.currency === currency
    ? feePreview.fee_amount
    : 0;
  const totalDebit = parsedAmount + feeAmount;
  const remainingAfter = Math.max(available - totalDebit, 0);
  const insufficientBalance = totalDebit > 0 && totalDebit > available;

  const primaryPhone = getPrimaryPhone(phoneQuery.data, method);
  const secondaryPhone = getSecondaryPhone(phoneQuery.data, method);
  const activePhone = phoneChoice === 2 && secondaryPhone ? secondaryPhone : primaryPhone;

  // ----- submit handlers -----
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setOtpNeeded(null);
    if (parsedAmount <= 0) {
      setError("Veuillez saisir un montant valide.");
      return;
    }
    if (insufficientBalance) {
      setError(
        `Solde insuffisant. Débit prévu : ${formatMajor(totalDebit, currency)} · Disponible : ${formatMajor(available, currency)}.`,
      );
      return;
    }
    if (!activePhone || activePhone === "Non configuré") {
      setError(
        `Aucun numéro de réception configuré pour ${METHOD_LABEL[method]}. Configurez-le d'abord dans Paramètres.`,
      );
      return;
    }

    setSubmitting(true);
    try {
      const resp = await initiateWithdrawal({
        amount: parsedAmount,
        currency,
        method,
        phone_choice: phoneChoice,
      });
      if (resp.status === "otp_required") {
        setOtpNeeded(resp as WithdrawalOtpRequired);
        setOtpCode("");
        setOtpAttempts(0);
        setOtpError(null);
        setResendCooldown(180);
      } else {
        setSuccess(resp as WithdrawalPayoutResponse);
        setAmount("");
        setFeePreview(null);
        balanceQuery.refetch();
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const submitWithOtp = async () => {
    if (otpCode.length !== 6) {
      setOtpError("Saisissez le code OTP à 6 chiffres.");
      return;
    }
    setOtpSubmitting(true);
    setOtpError(null);
    try {
      const resp = await initiateWithdrawal({
        amount: parsedAmount,
        currency,
        method,
        phone_choice: phoneChoice,
        otp_code: otpCode,
        reference: otpNeeded?.reference,
        otp_reference: otpNeeded?.reference,
      });
      if (resp.status === "otp_required") {
        setOtpAttempts((a) => a + 1);
        setOtpError("Code OTP incorrect. Réessayez.");
      } else {
        setSuccess(resp as WithdrawalPayoutResponse);
        setAmount("");
        setFeePreview(null);
        setOtpNeeded(null);
        setOtpCode("");
        balanceQuery.refetch();
      }
    } catch (err) {
      setOtpAttempts((a) => a + 1);
      setOtpError(errorMessage(err));
    } finally {
      setOtpSubmitting(false);
    }
  };

  const doResendOtp = async () => {
    if (!otpNeeded?.reference || resendCooldown > 0) return;
    try {
      await resendWithdrawalOtp(otpNeeded.reference);
      setResendCooldown(180);
      setOtpError(null);
    } catch (err) {
      setOtpError(errorMessage(err));
    }
  };

  return (
    <div>
      <PageHeader
        title="Retrait"
        breadcrumbs={[{ label: "Transactions" }, { label: "Retrait" }]}
        actions={
          <button
            type="button"
            onClick={() => navigate("/historique-de-transactions?type=retrait")}
            className="btn btn-light btn-sm"
          >
            Historique des retraits
            <ArrowRight size={14} />
          </button>
        }
      />

      {/* Balance strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {(["CDF", "USD"] as const).map((c) => {
          const total = Number(balancesByCurrency[c] ?? 0);
          const gradient =
            c === "USD" ? "from-emerald-500 to-emerald-700" : "from-sky-500 to-sky-700";
          const isActive = currency === c;
          return (
            <button
              key={c}
              type="button"
              onClick={() => setCurrency(c)}
              className={`card overflow-hidden relative bg-gradient-to-br ${gradient} text-left transition-transform ${
                isActive ? "ring-2 ring-offset-2 ring-brand-500 scale-[1.01]" : "opacity-80 hover:opacity-100"
              }`}
            >
              <div className="px-6 py-5 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Wallet size={16} className="opacity-90" />
                    <div className="text-sm opacity-90">Solde disponible {c}</div>
                  </div>
                  <span className="text-[10px] font-bold tracking-wider bg-white/20 rounded px-2 py-0.5">
                    {c}
                  </span>
                </div>
                <div className="text-3xl font-bold mt-2 truncate">{formatMajor(total, c)}</div>
                <div className="text-xs opacity-80 mt-1">
                  {wallets.filter((w) => w.currency === c).length} wallet
                  {wallets.filter((w) => w.currency === c).length > 1 ? "s" : ""} · toutes méthodes
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Form */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <div>
                <div className="card-title">Nouveau retrait</div>
                <div className="card-subtitle">
                  Débit du wallet {currency} vers le numéro {METHOD_LABEL[method]} configuré.
                </div>
              </div>
            </div>
            <form onSubmit={submit} className="card-body space-y-5">
              {/* Amount */}
              <div>
                <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">
                  Montant à retirer ({currency})
                </label>
                <div className="relative">
                  <input
                    type="number"
                    inputMode="decimal"
                    step={currency === "USD" ? "0.01" : "1"}
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={currency === "USD" ? "0.00" : "0"}
                    className="form-input py-3 pr-24 text-lg font-semibold"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-[#7e8299]">
                    {currency}
                  </div>
                </div>
                {feePreview && parsedAmount > 0 && (
                  <div className="mt-3 rounded-lg bg-[#f5f8fa] border border-[#eff2f5] p-3 text-xs space-y-1">
                    <div className="flex justify-between">
                      <span className="text-[#7e8299]">Frais {feePreview.fee_label}</span>
                      <span className="font-semibold text-[#3f4254]">
                        {formatMajor(feePreview.fee_amount, currency)} ({feePreview.fee_rate_pct.toFixed(2)}%)
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#7e8299]">Débit total wallet</span>
                      <span className="font-semibold text-[#181c32]">
                        {formatMajor(feePreview.total_to_pay, currency)}
                      </span>
                    </div>
                    <div className="flex justify-between border-t border-[#eff2f5] pt-1 mt-1">
                      <span className="text-[#7e8299]">Reçu par le client</span>
                      <span className="font-semibold text-success-700">
                        {formatMajor(feePreview.amount_customer_receives, currency)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#7e8299]">Solde restant après retrait</span>
                      <span className={`font-semibold ${insufficientBalance ? "text-danger-700" : "text-[#181c32]"}`}>
                        {formatMajor(remainingAfter, currency)}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Method selector */}
              <div>
                <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">
                  Méthode de retrait
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(["mpesa", "orange", "airtel"] as Method[]).map((m) => {
                    const active = method === m;
                    return (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setMethod(m)}
                        className={`flex flex-col items-center gap-1.5 rounded-xl border p-3 transition-colors ${
                          active
                            ? "border-brand-500 bg-brand-50 text-brand-700"
                            : "border-slate-200 hover:border-slate-300 text-[#3f4254]"
                        }`}
                      >
                        <span
                          className="w-3 h-3 rounded-full"
                          style={{ background: METHOD_COLOR[m] }}
                        />
                        <span className="text-sm font-semibold">{METHOD_LABEL[m]}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Phone choice + display */}
              <div>
                <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">
                  Numéro de réception
                </label>
                <div className="rounded-xl border border-slate-200 p-3">
                  <div className="flex items-center gap-3">
                    <Phone size={18} className="text-brand-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-base font-semibold text-[#181c32]">
                        {activePhone || "Non configuré"}
                      </div>
                      <div className="text-xs text-[#7e8299]">
                        {METHOD_LABEL[method]} · numéro {phoneChoice === 2 ? "secondaire" : "principal"}
                      </div>
                    </div>
                    {secondaryPhone && (
                      <div className="inline-flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
                        <button
                          type="button"
                          onClick={() => setPhoneChoice(1)}
                          className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                            phoneChoice === 1 ? "bg-white text-brand-700 shadow-sm" : "text-slate-500"
                          }`}
                        >
                          #1
                        </button>
                        <button
                          type="button"
                          onClick={() => setPhoneChoice(2)}
                          className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${
                            phoneChoice === 2 ? "bg-white text-brand-700 shadow-sm" : "text-slate-500"
                          }`}
                        >
                          #2
                        </button>
                      </div>
                    )}
                  </div>
                  {phoneQuery.isError && (
                    <div className="mt-2 text-xs text-warning-700 bg-warning-50 rounded p-2 flex items-start gap-1.5">
                      <AlertTriangle size={12} className="mt-0.5 shrink-0" />
                      Configuration des numéros non disponible pour l'instant.
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 rounded-lg border border-danger-200 bg-danger-50 p-3 text-xs text-danger-700">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  <div>{error}</div>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting || parsedAmount <= 0 || insufficientBalance}
                className="btn btn-primary w-full py-3"
              >
                <Send size={16} />
                {submitting ? "Envoi en cours…" : `Retirer ${parsedAmount > 0 ? formatMajor(parsedAmount, currency) : ""}`}
              </button>
            </form>
          </div>
        </div>

        {/* Right column: recap + info */}
        <div className="space-y-5">
          <div className="card">
            <div className="card-body">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck size={16} className="text-brand-500" />
                <div className="font-semibold text-[#181c32] text-sm">Récapitulatif</div>
              </div>
              <dl className="space-y-2 text-sm">
                <Row label="Devise" value={currency} />
                <Row label="Méthode" value={METHOD_LABEL[method]} />
                <Row label="Montant" value={parsedAmount > 0 ? formatMajor(parsedAmount, currency) : "—"} />
                <Row
                  label="Frais"
                  value={feePreview ? formatMajor(feePreview.fee_amount, currency) : "—"}
                />
                <Row
                  label="Débit total"
                  value={feePreview ? formatMajor(feePreview.total_to_pay, currency) : "—"}
                  strong
                />
                <Row label="Numéro" value={activePhone || "—"} mono />
              </dl>
            </div>
          </div>

          <WalletBreakdown wallets={wallets} currency={currency} />

          <div className="rounded-lg bg-brand-50 border border-brand-100 p-3 text-xs text-brand-700 flex gap-2">
            <Info size={14} className="shrink-0 mt-0.5" />
            <div>
              Selon le montant et l'opérateur, un code OTP par SMS peut être demandé pour valider
              le retrait. Assurez-vous que le numéro de réception est actif.
            </div>
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      {otpNeeded && (
        <Modal
          open
          onClose={() => setOtpNeeded(null)}
          title="Validation OTP"
          subtitle={otpNeeded.message || "Entrez le code reçu par SMS."}
          size="sm"
          closeOnOverlay={false}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[#4a4a6a] mb-2">
                Code OTP (6 chiffres)
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                autoFocus
                className="form-input text-center text-2xl tracking-widest font-mono py-3"
              />
              {otpAttempts > 0 && (
                <div className="text-xs text-[#7e8299] mt-1">Tentative {otpAttempts + 1} / 3</div>
              )}
            </div>

            {otpError && (
              <div className="flex items-start gap-2 rounded-lg border border-danger-200 bg-danger-50 p-3 text-xs text-danger-700">
                <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                <div>{otpError}</div>
              </div>
            )}

            <button
              type="button"
              onClick={submitWithOtp}
              disabled={otpSubmitting || otpCode.length !== 6}
              className="btn btn-primary w-full"
            >
              {otpSubmitting ? "Validation…" : "Valider et confirmer le retrait"}
            </button>

            <button
              type="button"
              onClick={doResendOtp}
              disabled={resendCooldown > 0}
              className="text-xs text-brand-600 hover:text-brand-800 w-full text-center disabled:text-slate-400"
            >
              {resendCooldown > 0
                ? `Renvoi possible dans ${resendCooldown}s`
                : "Renvoyer un nouveau code"}
            </button>
          </div>
        </Modal>
      )}

      {/* Success card */}
      {success && (
        <Modal
          open
          onClose={() => setSuccess(null)}
          title={success.status === "pending" ? "Retrait en attente" : "Retrait envoyé"}
          size="md"
        >
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={40} className="text-success-600 shrink-0" />
              <div>
                <div className="font-semibold text-[#181c32]">{success.message}</div>
                {success.status === "pending" && (
                  <div className="text-xs text-[#7e8299] mt-0.5">
                    En attente de confirmation du réseau {METHOD_LABEL[method]}. Vous verrez le
                    statut final dans l'historique.
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 p-3 text-xs space-y-1.5">
              <Row label="Référence" value={success.reference} mono strong />
              <Row label="Montant" value={formatMajor(success.amount, success.currency)} strong />
              <Row label="Méthode" value={METHOD_LABEL[(success.method as Method) ?? method]} />
              {success.switch_reference && (
                <Row label="Réf. switch" value={success.switch_reference} mono />
              )}
            </div>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setSuccess(null);
                  navigate("/historique-de-transactions?type=retrait");
                }}
                className="btn btn-primary flex-1"
              >
                Voir l'historique
              </button>
              <button type="button" onClick={() => setSuccess(null)} className="btn btn-light">
                Fermer
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Row({
  label,
  value,
  mono,
  strong,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  strong?: boolean;
}) {
  return (
    <div className="flex justify-between items-center gap-3">
      <dt className="text-xs uppercase tracking-wider text-[#7e8299] font-bold shrink-0">
        {label}
      </dt>
      <dd
        className={`text-right truncate ${mono ? "font-mono text-xs" : "text-sm"} ${
          strong ? "font-semibold text-[#181c32]" : "text-[#3f4254]"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

function WalletBreakdown({
  wallets,
  currency,
}: {
  wallets: MerchantBalanceResponse["wallets"];
  currency: Currency;
}) {
  const [expanded, setExpanded] = useState(false);
  const items = wallets.filter((w) => w.currency === currency);
  if (items.length === 0) return null;
  return (
    <div className="card">
      <div className="card-body">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center justify-between w-full text-sm font-semibold text-[#181c32]"
        >
          <span>Détail par opérateur ({currency})</span>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
        {expanded && (
          <div className="mt-3 space-y-2">
            {items.map((w) => (
              <div key={w.wallet_id} className="flex justify-between text-xs">
                <span className="capitalize text-[#7e8299]">{w.method}</span>
                <span className="font-mono font-semibold text-[#181c32]">
                  {formatMajor(Number(w.balance ?? 0), currency)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function getPrimaryPhone(config: MerchantPhoneConfig | undefined, method: Method): string {
  if (!config) return "";
  const map: Record<Method, string | undefined> = {
    mpesa: config.method_mpesa,
    orange: config.method_orange,
    airtel: config.method_airtel,
  };
  return map[method] ?? "";
}

function getSecondaryPhone(config: MerchantPhoneConfig | undefined, method: Method): string {
  if (!config?.dual_withdrawal_numbers) return "";
  const map: Record<Method, string | undefined> = {
    mpesa: config.method_mpesa_2,
    orange: config.method_orange_2,
    airtel: config.method_airtel_2,
  };
  return map[method] ?? "";
}

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    // Certains endpoints POS renvoient {detail: {message: "..."}} au lieu de string
    const anyExtra = (err.extra as Record<string, unknown>) ?? {};
    const detailObj = anyExtra.detail as { message?: string } | undefined;
    if (detailObj?.message) return detailObj.message;
    return err.detail || err.title || "Erreur lors du retrait";
  }
  if (err instanceof Error) return err.message;
  return "Erreur lors du retrait";
}
